import React, { useEffect, useState } from 'react';
import {
	Grid2,
	Paper,
	styled,
	Table,
	TableBody,
	TableCell,
	tableCellClasses,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	IconButton as IconBtn,
	TablePagination,
} from '@mui/material';
import SuperAdminSideNavigationBar from '../../components/SuperAdminSideNavigationBar';
import { useMainCustomStyle } from '../../assets/theme/Theme';
import AlertDialog from '../../components/AlertDialog';
import { DeleteAdIcon, EditAdIcon } from '../../assets/icons/Icons';
import { Link } from 'react-router-dom';
import {
	deleteAdmin,
	fetchAllAdmins,
	updateAdmin,
} from '../../services/UserService';
import { getAccessToken } from '../../Utils/AuthUtil';
import { toast } from 'react-toastify';
import UserControlPopup from '../../components/UserControlPopup';
import { USER_TYPES } from '../../Utils/Enums';
import { Admin } from '../../types';

const INITIAL_USER_STATE = {
	id: '',
	name: '',
	userType: '',
};

function AgencyAdmins() {
	const mainDivStyle = useMainCustomStyle();
	const token = getAccessToken();
	const [userData, setUserData] = useState<Array<Admin>>([]);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState(INITIAL_USER_STATE);
	const [loading, setLoading] = useState(false);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [page, setPage] = useState(0);
	const [accTypeError, setAccTypeError] = useState({
		status: false,
		msg: '',
	});
	const [isNotEdited, setIsNotEdited] = useState(true);

	useEffect(() => {
		getAllUsers();
	}, []);

	async function getAllUsers() {
		const data = await fetchAllAdmins(token ? token : '');
		setUserData(data.content.reverse());
	}

	async function removeUser(id: string) {
		const response = await deleteAdmin(id, token ? token : '');
		if (response.message === 'Success') {
			getAllUsers();
			toast.success('User removed Successfully');
		} else {
			toast.success(response.message);
		}
	}

	async function updateUserDetails() {
		const userType = USER_TYPES.find(
			item => item.name === selectedUser.userType,
		);

		if (userType) {
			const body = {
				userType: userType.key,
			};
			const response = await updateAdmin(
				selectedUser.id,
				token ? token : '',
				body,
			);
			if (response.message === 'Success') {
				getAllUsers();
				toast.success('User updated Successfully');
				setIsModalOpen(false);
			} else {
				toast.error(response.message);
			}
		} else {
			toast.success('No selected user type exists');
		}
	}

	const handleChangePage = (
		event: React.MouseEvent<HTMLButtonElement> | null,
		newPage: number,
	) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
	) => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};

	function onOpenUserControls(id: string, name: string, type: string) {
		const user = USER_TYPES.find(item => item.key === type);
		setSelectedUser({
			id,
			name,
			userType: user ? user.name : 'N/A',
		});
		setIsModalOpen(true);
	}

	function getUserName(label: string) {
		if (!label) {
			return 'N/A';
		}

		const type = USER_TYPES.find(item => item.key === label);
		return type ? type.name : 'N/A';
	}

	function onChangeUserType(key: string) {
		setSelectedUser(prevState => ({ ...prevState, userType: key }));
		setIsNotEdited(false);
	}

	function onCloseModal() {
		setIsModalOpen(false);
		setSelectedUser(INITIAL_USER_STATE);
		setIsNotEdited(true);
	}

	return (
		<>
			<SuperAdminSideNavigationBar />
			<div style={mainDivStyle}>
				<Grid2 container paddingLeft={'20px'} paddingRight={'20px'}>
					<Grid2 component={'div'} paddingLeft="30px">
						<Typography
							variant="h6"
							fontWeight={'600'}
							color="#28696D">
							App users
						</Typography>
					</Grid2>
					<Grid2 container width={'100%'} paddingLeft="30px">
						<TableContainer
							component={Paper}
							style={{ boxShadow: 'none', marginTop: '30px' }}>
							<Table
								sx={{ minWidth: 700 }}
								aria-label="customized table">
								<TableHead>
									<TableRow>
										<StyledTableCell align="center">
											<TableHeaderText>
												Username
											</TableHeaderText>
										</StyledTableCell>
										<StyledTableCell align="center">
											<TableHeaderText>
												Email
											</TableHeaderText>
										</StyledTableCell>
										<StyledTableCell align="center">
											<TableHeaderText>
												User type
											</TableHeaderText>
										</StyledTableCell>
										<StyledTableCell align="center">
											<TableHeaderText>
												Created on
											</TableHeaderText>
										</StyledTableCell>
										<StyledTableCell align="center">
											<TableHeaderText>
												Actions
											</TableHeaderText>
										</StyledTableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{userData && userData.length > 0 ? (
										userData
											.slice(
												page * rowsPerPage,
												page * rowsPerPage +
													rowsPerPage,
											)
											.map((admin, index) => {
												return (
													<StyledTableRow key={index}>
														<StyledTableCell align="center">
															<TableRowText>
																{admin.username}
															</TableRowText>
														</StyledTableCell>
														<StyledTableCell align="center">
															<TableRowText>
																{admin.email}
															</TableRowText>
														</StyledTableCell>
														<StyledTableCell align="center">
															<TableRowText>
																{getUserName(
																	admin.userType,
																)}
															</TableRowText>
														</StyledTableCell>
														<StyledTableCell align="center">
															<TableRowText>
																{new Date(
																	admin.joinedDate,
																)
																	.getDate()
																	.toString()
																	.padStart(
																		2,
																		'0',
																	) +
																	'/' +
																	(
																		new Date(
																			admin.joinedDate,
																		).getMonth() +
																		1
																	)
																		.toString()
																		.padStart(
																			2,
																			'0',
																		) +
																	'/' +
																	new Date(
																		admin.joinedDate,
																	)
																		.getFullYear()
																		.toString()}
															</TableRowText>
														</StyledTableCell>
														<StyledTableCell align="center">
															<IconBtn
																sx={{
																	marginRight:
																		'3px',
																}}
																onClick={() =>
																	onOpenUserControls(
																		admin.id,
																		admin.username,
																		admin.userType,
																	)
																}>
																<EditAdIcon />
															</IconBtn>
															<AlertDialog
																id={admin.id}
																title={
																	'Remove User'
																}
																bodyContent={
																	'Removing admin will permanently delete all the data.'
																}
																icon={DeleteAdIcon()}
																deleteData={
																	removeUser
																}
																cancelBtnText={
																	'Keep the admin'
																}
																deleteBtnText={
																	'Remove user'
																}
															/>
														</StyledTableCell>
													</StyledTableRow>
												);
											})
									) : (
										<StyledTableRow>
											<StyledTableCell
												colSpan={6}
												align="center">
												<TableRowText>
													No data available
												</TableRowText>
											</StyledTableCell>
										</StyledTableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
						<TablePagination
							rowsPerPageOptions={[]}
							component="div"
							count={userData && userData.length}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
						/>
					</Grid2>
				</Grid2>
			</div>
			<UserControlPopup
				isModalOpen={isModalOpen}
				selectedUser={selectedUser}
				onClickBtn={updateUserDetails}
				onChangeUserType={onChangeUserType}
				onClose={onCloseModal}
				accTypeError={accTypeError}
				isButtonDisabled={isNotEdited}
			/>
		</>
	);
}

const StyledTableCell = styled(TableCell)(() => ({
	[`&.${tableCellClasses.head}`]: {
		backgroundColor: '#FFFFFF',
		color: 'rgba(0, 0, 0, 0.5)',
		paddingTop: '20px',
		fontSize: 12,
		fontWeight: 600,
	},
	[`&.${tableCellClasses.body}`]: {
		fontSize: 12,
		fontWeight: 400,
		color: '#000000',
	},
}));

const StyledTableRow = styled(TableRow)(() => ({
	'&:nth-of-type(odd)': {
		background:
			'linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), #C2C9D1',
	},
	// hide last border
	'&:last-child td, &:last-child th': {
		border: 0,
	},
}));

const TableHeaderText = styled(Typography)(() => ({
	fontSize: '12px',
	fontWeight: '600',
	color: 'rgba(0, 0, 0, 0.5)',
}));

const TableRowText = styled(Typography)(() => ({
	fontSize: '12px',
	fontWeight: '400',
	color: 'rgba(0, 0, 0, 1)',
}));

export default AgencyAdmins;
